import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';

import {
  Subgroup,
  SubgroupApplyResult,
  SubgroupConfig,
  useApplySubgroup,
  useUpdateSubgroup,
  type ApplyPath
} from '../../api/useSubgroups';

/**
 * Edit the subgroup's `config` SNAPSHOT (the four field sections) and, with a
 * prominent "Apply to subgroup" button, stamp it onto every member rule. Save
 * persists the snapshot ONLY — drift management stays manual (no auto-prompt
 * after a snapshot edit, per the owner decision). The Apply result
 * (`{matched, modified, skipped}`) is surfaced inline.
 */

interface Props {
  open: boolean;
  subgroup: Subgroup | null;
  onClose: () => void;
}

/** A blank text field maps to "unset" (the key is omitted from the snapshot),
 *  so Apply leaves that field untouched on each member rule. */
const numOrUndef = (raw: string): number | undefined => {
  const t = raw.trim();
  if (!t) return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
};

const toStr = (v: number | null | undefined): string =>
  v == null ? '' : String(v);

const SECTION_SX = { fontSize: '0.8rem', fontWeight: 600, mb: 0.5 } as const;
const FIELD_SX = {
  '& .MuiOutlinedInput-root': { fontSize: '0.8rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8rem' }
} as const;

export function SubgroupConfigDialog({ open, subgroup, onClose }: Props) {
  const updateMutation = useUpdateSubgroup();
  const applyMutation = useApplySubgroup();

  // Raw string form state; converted to a typed snapshot on save. Maker flags
  // are tri-state (on / off / unset) — modeled as an explicit boolean | null.
  const [entryStrategy, setEntryStrategy] = useState('');
  const [counterHedgeMode, setCounterHedgeMode] = useState('');
  const [counterStuckSeconds, setCounterStuckSeconds] = useState('');
  // Tri-state (on / off / unset). Unset (null) is omitted from the snapshot so
  // Apply leaves the field untouched on member rules — mirrors the maker flags.
  const [disablePlacementGate, setDisablePlacementGate] = useState<
    boolean | null
  >(null);
  const [walkStepSeconds, setWalkStepSeconds] = useState('');
  const [walkStepCents, setWalkStepCents] = useState('');
  const [polyYesMaker, setPolyYesMaker] = useState<boolean | null>(null);
  const [polyNoMaker, setPolyNoMaker] = useState<boolean | null>(null);
  const [kalshiYesMaker, setKalshiYesMaker] = useState<boolean | null>(null);
  const [kalshiNoMaker, setKalshiNoMaker] = useState<boolean | null>(null);
  const [size, setSize] = useState('');
  const [maxVolumeContracts, setMaxVolumeContracts] = useState('');
  const [volumePeriod, setVolumePeriod] = useState('');
  const [maxOrderSize, setMaxOrderSize] = useState('');
  const [makerSizeMode, setMakerSizeMode] = useState('');
  const [thresholdSum, setThresholdSum] = useState('');
  const [lockstepMaxAheadBatches, setLockstepMaxAheadBatches] = useState('');
  const [applyPath, setApplyPath] = useState<ApplyPath>('both');
  const [applyResult, setApplyResult] = useState<SubgroupApplyResult | null>(
    null
  );

  // Hydrate the form from the subgroup's snapshot each time the dialog opens
  // for a (different) subgroup.
  useEffect(() => {
    if (!open || !subgroup) return;
    const c = subgroup.config ?? {};
    setEntryStrategy(c.entryStrategy ?? '');
    setCounterHedgeMode(c.counterHedgeMode ?? '');
    setCounterStuckSeconds(toStr(c.counterStuckMarketHedgeSeconds));
    setDisablePlacementGate(c.disablePlacementGate ?? null);
    setWalkStepSeconds(toStr(c.counterMakerWalkStepSeconds));
    setWalkStepCents(toStr(c.counterMakerWalkStepCents));
    setPolyYesMaker(c.polyYesMaker ?? null);
    setPolyNoMaker(c.polyNoMaker ?? null);
    setKalshiYesMaker(c.kalshiYesMaker ?? null);
    setKalshiNoMaker(c.kalshiNoMaker ?? null);
    setSize(toStr(c.size));
    setMaxVolumeContracts(toStr(c.maxVolumeContracts));
    setVolumePeriod(c.volumePeriod ?? '');
    setMaxOrderSize(toStr(c.maxOrderSize));
    setMakerSizeMode(c.makerSizeMode ?? '');
    setThresholdSum(toStr(c.thresholdSum));
    setLockstepMaxAheadBatches(toStr(c.lockstepMaxAheadBatches));
    setApplyResult(null);
    setApplyPath('both');
  }, [open, subgroup]);

  // Build a snapshot omitting every unset field. An empty maker flag (null) is
  // omitted; an explicit true/false is kept so Apply can toggle it.
  const snapshot = useMemo<SubgroupConfig>(() => {
    const c: SubgroupConfig = {};
    if (entryStrategy)
      c.entryStrategy = entryStrategy as SubgroupConfig['entryStrategy'];
    if (counterHedgeMode)
      c.counterHedgeMode =
        counterHedgeMode as SubgroupConfig['counterHedgeMode'];
    const stuck = numOrUndef(counterStuckSeconds);
    if (stuck !== undefined) c.counterStuckMarketHedgeSeconds = stuck;
    if (disablePlacementGate !== null)
      c.disablePlacementGate = disablePlacementGate;
    const walkS = numOrUndef(walkStepSeconds);
    if (walkS !== undefined) c.counterMakerWalkStepSeconds = walkS;
    const walkC = numOrUndef(walkStepCents);
    if (walkC !== undefined) c.counterMakerWalkStepCents = walkC;
    if (polyYesMaker !== null) c.polyYesMaker = polyYesMaker;
    if (polyNoMaker !== null) c.polyNoMaker = polyNoMaker;
    if (kalshiYesMaker !== null) c.kalshiYesMaker = kalshiYesMaker;
    if (kalshiNoMaker !== null) c.kalshiNoMaker = kalshiNoMaker;
    const sz = numOrUndef(size);
    if (sz !== undefined) c.size = sz;
    const mvc = numOrUndef(maxVolumeContracts);
    if (mvc !== undefined) c.maxVolumeContracts = mvc;
    if (volumePeriod)
      c.volumePeriod = volumePeriod as SubgroupConfig['volumePeriod'];
    const mos = numOrUndef(maxOrderSize);
    if (mos !== undefined) c.maxOrderSize = mos;
    if (makerSizeMode)
      c.makerSizeMode = makerSizeMode as SubgroupConfig['makerSizeMode'];
    const ts = numOrUndef(thresholdSum);
    if (ts !== undefined) c.thresholdSum = ts;
    const lock = numOrUndef(lockstepMaxAheadBatches);
    if (lock !== undefined) c.lockstepMaxAheadBatches = lock;
    return c;
  }, [
    entryStrategy,
    counterHedgeMode,
    counterStuckSeconds,
    disablePlacementGate,
    walkStepSeconds,
    walkStepCents,
    polyYesMaker,
    polyNoMaker,
    kalshiYesMaker,
    kalshiNoMaker,
    size,
    maxVolumeContracts,
    volumePeriod,
    maxOrderSize,
    makerSizeMode,
    thresholdSum,
    lockstepMaxAheadBatches
  ]);

  if (!subgroup) return null;

  const handleSave = async () => {
    await updateMutation
      .mutateAsync({
        id: subgroup._id,
        groupId: subgroup.groupId,
        dto: { config: snapshot }
      })
      .catch(() => {});
  };

  // Apply saves the snapshot first (so the engine sees what the badge shows)
  // then stamps it onto member rules. A failed save aborts the apply; both
  // failures are surfaced by the global MutationCache.onError backstop.
  const handleApply = async () => {
    setApplyResult(null);
    try {
      await updateMutation.mutateAsync({
        id: subgroup._id,
        groupId: subgroup.groupId,
        dto: { config: snapshot }
      });
      const result = await applyMutation.mutateAsync({
        id: subgroup._id,
        groupId: subgroup.groupId,
        path: applyPath
      });
      setApplyResult(result);
    } catch {
      /* surfaced by the backstop */
    }
  };

  const busy = updateMutation.isLoading || applyMutation.isLoading;

  // 3-state maker flag control: a checkbox + a "set" toggle. Unchecked "set"
  // means "leave as-is" (null); checking it sets an explicit on/off.
  const makerFlagRow = (
    label: string,
    value: boolean | null,
    set: (v: boolean | null) => void,
    onLabel = 'Maker'
  ) => (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography sx={{ fontSize: '0.75rem', width: 90 }}>{label}</Typography>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={value !== null}
            onChange={e => set(e.target.checked ? false : null)}
          />
        }
        label={<Typography sx={{ fontSize: '0.7rem' }}>Set</Typography>}
      />
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            disabled={value === null}
            checked={value === true}
            onChange={e => set(e.target.checked)}
          />
        }
        label={<Typography sx={{ fontSize: '0.7rem' }}>{onLabel}</Typography>}
      />
    </Stack>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: '1rem' }}>
        Subgroup config — {subgroup.name}
      </DialogTitle>
      <DialogContent dividers>
        <Typography
          sx={{ fontSize: '0.72rem', color: 'grey.500', mb: 1.5 }}
        >
          Blank fields are left untouched on member rules when you Apply. Save
          stores the snapshot only; press Apply to stamp it onto every rule in
          this subgroup.
        </Typography>

        {/* Aggression / go-to-market */}
        <Typography sx={SECTION_SX}>Aggression / go-to-market</Typography>
        <Stack spacing={1.2} sx={{ mb: 1.5 }}>
          <TextField
            select
            size="small"
            label="Entry strategy"
            value={entryStrategy}
            onChange={e => setEntryStrategy(e.target.value)}
            sx={FIELD_SX}
          >
            <MenuItem value="">— leave as-is —</MenuItem>
            <MenuItem value="maker-first">maker-first</MenuItem>
            <MenuItem value="taker-taker">taker-taker</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            label="Counter hedge mode"
            value={counterHedgeMode}
            onChange={e => setCounterHedgeMode(e.target.value)}
            sx={FIELD_SX}
          >
            <MenuItem value="">— leave as-is —</MenuItem>
            <MenuItem value="always-threshold">always-threshold</MenuItem>
            <MenuItem value="gtc-to-sum-one">gtc-to-sum-one</MenuItem>
            <MenuItem value="market-to-hedge">market-to-hedge</MenuItem>
          </TextField>
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              type="number"
              label="Stuck mkt-hedge (s)"
              value={counterStuckSeconds}
              onChange={e => setCounterStuckSeconds(e.target.value)}
              sx={FIELD_SX}
            />
            <TextField
              size="small"
              type="number"
              label="Walk step (s)"
              value={walkStepSeconds}
              onChange={e => setWalkStepSeconds(e.target.value)}
              sx={FIELD_SX}
            />
            <TextField
              size="small"
              type="number"
              label="Walk step (¢)"
              value={walkStepCents}
              onChange={e => setWalkStepCents(e.target.value)}
              sx={FIELD_SX}
            />
          </Stack>
          {makerFlagRow(
            'No gate',
            disablePlacementGate,
            setDisablePlacementGate,
            'On'
          )}
          <Typography sx={{ fontSize: '0.66rem', color: 'grey.500' }}>
            Disable placement gate (keep posting far from threshold — illiquid).
          </Typography>
        </Stack>
        <Divider sx={{ my: 1 }} />

        {/* Maker-leg flags */}
        <Typography sx={SECTION_SX}>Maker-leg flags</Typography>
        <Stack spacing={0.3} sx={{ mb: 1.5 }}>
          {makerFlagRow('Poly YES', polyYesMaker, setPolyYesMaker)}
          {makerFlagRow('Poly NO', polyNoMaker, setPolyNoMaker)}
          {makerFlagRow('Kalshi YES', kalshiYesMaker, setKalshiYesMaker)}
          {makerFlagRow('Kalshi NO', kalshiNoMaker, setKalshiNoMaker)}
        </Stack>
        <Divider sx={{ my: 1 }} />

        {/* Sizing & limits */}
        <Typography sx={SECTION_SX}>Sizing &amp; limits</Typography>
        <Stack spacing={1.2} sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              type="number"
              label="Size"
              value={size}
              onChange={e => setSize(e.target.value)}
              sx={FIELD_SX}
            />
            <TextField
              size="small"
              type="number"
              label="Max volume"
              value={maxVolumeContracts}
              onChange={e => setMaxVolumeContracts(e.target.value)}
              sx={FIELD_SX}
            />
            <TextField
              size="small"
              type="number"
              label="Max order size"
              value={maxOrderSize}
              onChange={e => setMaxOrderSize(e.target.value)}
              sx={FIELD_SX}
            />
          </Stack>
          <Stack direction="row" spacing={1}>
            <TextField
              select
              size="small"
              label="Volume period"
              value={volumePeriod}
              onChange={e => setVolumePeriod(e.target.value)}
              sx={{ ...FIELD_SX, minWidth: 140 }}
            >
              <MenuItem value="">— leave as-is —</MenuItem>
              <MenuItem value="hour">hour</MenuItem>
              <MenuItem value="day">day</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Maker size mode"
              value={makerSizeMode}
              onChange={e => setMakerSizeMode(e.target.value)}
              sx={{ ...FIELD_SX, minWidth: 140 }}
            >
              <MenuItem value="">— leave as-is —</MenuItem>
              <MenuItem value="dynamic">dynamic</MenuItem>
              <MenuItem value="fixed">fixed</MenuItem>
            </TextField>
          </Stack>
        </Stack>
        <Divider sx={{ my: 1 }} />

        {/* Thresholds & timing */}
        <Typography sx={SECTION_SX}>Thresholds &amp; timing</Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
          <TextField
            size="small"
            type="number"
            label="Threshold sum"
            value={thresholdSum}
            onChange={e => setThresholdSum(e.target.value)}
            sx={FIELD_SX}
            inputProps={{ step: '0.001' }}
          />
          <TextField
            size="small"
            type="number"
            label="Lockstep max-ahead"
            value={lockstepMaxAheadBatches}
            onChange={e => setLockstepMaxAheadBatches(e.target.value)}
            sx={FIELD_SX}
          />
        </Stack>

        {/* Apply path + result */}
        <Divider sx={{ my: 1 }} />
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ mt: 1 }}
        >
          <TextField
            select
            size="small"
            label="Apply threshold to path"
            value={applyPath}
            onChange={e => setApplyPath(e.target.value as ApplyPath)}
            sx={{ ...FIELD_SX, minWidth: 180 }}
          >
            <MenuItem value="both">Both paths</MenuItem>
            <MenuItem value="a">Path A</MenuItem>
            <MenuItem value="b">Path B</MenuItem>
          </TextField>
          {applyResult && (
            <Typography
              sx={{ fontSize: '0.78rem', color: 'success.light' }}
              role="status"
            >
              Applied: {applyResult.matched} matched ·{' '}
              {applyResult.modified} modified · {applyResult.skipped} skipped
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={busy} sx={{ fontSize: '0.78rem' }}>
          Close
        </Button>
        <Button
          variant="outlined"
          onClick={() => void handleSave()}
          disabled={busy}
          sx={{ fontSize: '0.78rem' }}
        >
          {updateMutation.isLoading && !applyMutation.isLoading ? (
            <CircularProgress size={14} />
          ) : (
            'Save snapshot'
          )}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => void handleApply()}
          disabled={busy}
          sx={{ fontSize: '0.78rem' }}
        >
          {applyMutation.isLoading ? (
            <CircularProgress size={14} color="inherit" />
          ) : (
            'Apply to subgroup'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
